// src/components/auth/EnvironmentSelector.js - Fixed for proper navigation

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server, CheckCircle, AlertCircle } from 'lucide-react';
import TenantVerificationService from '../../services/tenantVerificationService';
import { useAuth } from '../../auth/AuthContext';
import logger from '../../utils/logger';

const EnvironmentSelector = () => {
  const [environments, setEnvironments] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bcAccessDenied, setBcAccessDenied] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [redirectInProgress, setRedirectInProgress] = useState(false);
  
  const navigate = useNavigate();
  const { user, getAccessToken, updateActiveEnvironment, logout } = useAuth();
  
  // Debug function to log state
  const logDebugInfo = useCallback((message, data = {}) => {
    if (process.env.NODE_ENV === 'development') {
      logger.log(`EnvironmentSelector Debug - ${message}:`, data);
      setDebugInfo(prev => ({ ...prev, [message]: data }));
    }
  }, []);
  
  // Create service instance
  const tenantVerificationService = React.useMemo(() => {
    return new TenantVerificationService(getAccessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Fetch environments on component mount
  useEffect(() => {
    let isMounted = true;
    let redirectTimeout = null;
    
    const fetchEnvironments = async () => {
      if (redirectInProgress) return;
      
      logDebugInfo('Starting fetchEnvironments', { user });
      
      try {
        setIsLoading(true);
        setError(null);
        
        try {
          // First verify the user has access
          logDebugInfo('Verifying user access', { email: user?.email, oid: user?.oid });
          
          const verificationResult = await tenantVerificationService.verifyUserAccess(
            user?.email,
            user?.oid
          );
          
          logDebugInfo('Verification result', verificationResult);
          
          if (!isMounted) return;
          
          // Check if we need to redirect
          if (verificationResult.requiresRedirect || !verificationResult.exists || !verificationResult.isActive) {
            logDebugInfo('User verification failed, preparing for redirect', verificationResult);

            setRedirectInProgress(true);
            setBcAccessDenied(true);
            setError(verificationResult.message || "Your account is not active or not found.");
            setIsLoading(false);

            return;
          }
          
          // If active, get available environments
          logDebugInfo('Getting available environments');
          
          const environmentsData = await tenantVerificationService.getAvailableEnvironments();
          
          if (!isMounted) return;
          
          logDebugInfo('Environments data', environmentsData);
          
          if (!environmentsData.environments || environmentsData.environments.length === 0) {
            setError("No environments found for your account. Please contact your administrator.");
          } else {
            setEnvironments(environmentsData.environments);
            
            // If there's only one environment, auto-select it
            if (environmentsData.environments.length === 1 && isMounted) {
              setSelectedEnvironment(environmentsData.environments[0]);
            }
          }
        } catch (apiError) {
          if (!isMounted) return;
          
          logger.error('API error:', apiError);
          logDebugInfo('API error', { message: apiError.message, details: apiError });
          
          setRedirectInProgress(true);
          
          setError(apiError.message || "Failed to retrieve Business Central environments");
        }
      } catch (error) {
        if (!isMounted) return;
        
        logger.error('Error in environment selection:', error);
        logDebugInfo('Error in environment selection', { message: error.message, details: error });
        setError(error.message || "An unexpected error occurred");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    if (user && !redirectInProgress) {
      fetchEnvironments();
    } else if (!user && !redirectInProgress) {
      setIsLoading(false);
      setError("Authentication required. Please sign in again.");
      
      setRedirectInProgress(true);
    }
    
    return () => {
      isMounted = false;
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [user, tenantVerificationService, logDebugInfo, redirectInProgress, navigate]);
  
  // Handle environment selection
  const handleSelect = useCallback((environment) => {
    logDebugInfo('Environment selected', environment);
    setSelectedEnvironment(environment);
  }, [logDebugInfo]);
  
  // Handle confirmation - With client tenant companies fetch before navigation
  const handleContinue = useCallback(async () => {
    logDebugInfo('Continue button clicked', { selectedEnvironment });
    
    if (!selectedEnvironment) {
      setError("Please select an environment to continue");
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Check if environment has a company_Id
      const hasCompanyId = !!selectedEnvironment.company_Id && 
        selectedEnvironment.company_Id !== '00000000-0000-0000-0000-000000000000';
      
      // If there's no company_Id, we need to fetch client tenant companies to set it up,
      // but we'll still allow progression even if we can't set it up
      if (!hasCompanyId) {
        logDebugInfo('Environment has no company_Id, attempting to fetch client tenant companies');
        
        try {
          // Get token for user authentication
          const token = await getAccessToken();
          const registrationId = localStorage.getItem('registrationId');
          
          if (!token || !registrationId) {
            logger.warn('Missing authentication token or registration ID, but continuing anyway');
            logDebugInfo('Missing authentication token or registration ID', { token: !!token, registrationId: !!registrationId });
          } else {
            // Get the base API URL (including any necessary port) from the environment or use a default
            const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://demo-backend-demo.uksouth-01.azurewebsites.net/api';
            
            logDebugInfo('Attempting to fetch companies from client tenant via backend proxy', { 
              registrationId,
              environmentName: selectedEnvironment.name
            });
            
            // Call our backend API which will handle proxying to the client tenant
            try {
              const response = await fetch(`${apiBaseUrl}/bc/client-tenant/companies`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  environmentName: selectedEnvironment.name,
                  registrationId
                })
              });
              
              if (response.ok) {
                const companiesData = await response.json();
                logger.log('Client tenant companies:', companiesData);
                logDebugInfo('Client tenant companies fetched successfully', companiesData);
                
                // Check if the update was successful
                if (companiesData.updateResult && companiesData.updateResult.success) {
                  logDebugInfo('Company ID update successful', companiesData.updateResult);
                  
                  // Fetch the updated environment to get the new company_Id
                  try {
                    const refreshEnvironmentsData = await tenantVerificationService.getAvailableEnvironments();
                    const refreshedEnvironments = refreshEnvironmentsData.environments || [];
                    
                    // Find our environment in the refreshed data
                    const updatedEnvironment = refreshedEnvironments.find(env => env.id === selectedEnvironment.id);
                    
                    if (updatedEnvironment && updatedEnvironment.company_Id) {
                      // Update the selected environment
                      selectedEnvironment.company_Id = updatedEnvironment.company_Id;
                      logDebugInfo('Updated selectedEnvironment with new company_Id', { 
                        id: selectedEnvironment.id, 
                        company_Id: selectedEnvironment.company_Id 
                      });
                    } else {
                      logger.warn('Could not find updated environment with company_Id, but continuing anyway');
                    }
                  } catch (refreshError) {
                    logger.error('Error refreshing environments:', refreshError);
                    logDebugInfo('Error refreshing environments', { message: refreshError.message });
                  }
                } else {
                  logger.warn('Company ID update not successful, but continuing anyway');
                  logDebugInfo('Company ID update not successful', companiesData.updateResult || {});
                }
              } else {
                // Log the error but continue with portal navigation anyway
                try {
                  const errorJson = await response.json();
                  logger.error('Failed to fetch client tenant companies:', errorJson);
                  logDebugInfo('Failed to fetch client tenant companies', { 
                    status: response.status,
                    error: errorJson.message || errorJson.error || 'Unknown error'
                  });
                  
                  // Check if this is a missing refresh token error
                  const errorMessage = errorJson.message || errorJson.error || '';
                  const responseData = errorJson.responseData || {};
                  const bcErrorMessage = responseData.error?.message || '';
                  
                  // Look for specific patterns that indicate missing refresh token
                  const isAuthError = (
                    bcErrorMessage.includes('getClientToken') || 
                    errorMessage.includes('No HTTP resource was found') ||
                    errorMessage.includes('auth') || 
                    errorMessage.includes('token') ||
                    response.status === 401 || 
                    response.status === 403
                  );
                  
                  if (isAuthError) {
                    // Store auth incomplete flag for admin notification only for auth errors
                    localStorage.setItem('adminAuthIncomplete', 'true');
                    localStorage.setItem('adminAuthEnvironment', selectedEnvironment.name);
                    localStorage.setItem('adminAuthErrorCode', String(response.status));
                    logger.log('Authorization issue detected - missing refresh token. Flagging for admin.');
                  }

                } catch (e) {
                  // If we can't parse JSON, just log the raw text
                  const errorText = await response.text();
                  logger.error('Failed to fetch client tenant companies:', errorText);
                  logDebugInfo('Failed to fetch client tenant companies', { 
                    status: response.status,
                    responseText: errorText
                  });
                  
                  // Check if this is a missing refresh token error
                  const isAuthError = (
                    errorText.includes('getClientToken') || 
                    errorText.includes('No HTTP resource was found') ||
                    errorText.includes('auth') || 
                    errorText.includes('token') ||
                    response.status === 401 || 
                    response.status === 403
                  );
                  
                  if (isAuthError) {
                    // Store auth incomplete flag for admin notification only for auth errors
                    localStorage.setItem('adminAuthIncomplete', 'true');
                    localStorage.setItem('adminAuthEnvironment', selectedEnvironment.name);
                    localStorage.setItem('adminAuthErrorCode', String(response.status));
                    logger.log('Authorization issue detected - missing refresh token. Flagging for admin.');
                  }
                }
              }
            } catch (fetchError) {
              // Log the error but continue with portal navigation anyway
              logger.error('Error fetching client tenant companies:', fetchError);
              logDebugInfo('Error fetching client tenant companies', { message: fetchError.message });
              
              // Check if this is likely a refresh token/auth issue
              const errorMessage = fetchError.message || '';
              const isAuthError = (
                errorMessage.includes('getClientToken') || 
                errorMessage.includes('auth') || 
                errorMessage.includes('token') ||
                errorMessage.includes('unauthorized') ||
                errorMessage.includes('permission')
              );
              
              if (isAuthError) {
                // Store auth incomplete flag for admin notification only for auth errors
                localStorage.setItem('adminAuthIncomplete', 'true');
                localStorage.setItem('adminAuthEnvironment', selectedEnvironment.name);
                localStorage.setItem('adminAuthErrorCode', 'FETCH_ERROR');
                logger.log('Authorization issue detected - missing refresh token. Flagging for admin.');
              }
            }
          }
        } catch (setupError) {
          // Log the error but continue with portal navigation anyway
          logger.error('Error in company setup:', setupError);
          logDebugInfo('Error in company setup', { message: setupError.message });
          
          // Check if this is likely a refresh token issue
          const errorMessage = setupError.message || '';
          const isAuthError = (
            errorMessage.includes('getClientToken') || 
            errorMessage.includes('auth') || 
            errorMessage.includes('token') ||
            errorMessage.includes('unauthorized') ||
            errorMessage.includes('permission')
          );
          
          if (isAuthError) {
            // Store auth incomplete flag for admin notification only for auth errors
            localStorage.setItem('adminAuthIncomplete', 'true');
            localStorage.setItem('adminAuthEnvironment', selectedEnvironment.name);
            localStorage.setItem('adminAuthErrorCode', 'SETUP_ERROR');
            logger.log('Authorization issue detected - missing refresh token. Flagging for admin.');
          }
        }
      }
      
      // Ensure we have at least a placeholder company ID
      if (!selectedEnvironment.company_Id || 
          selectedEnvironment.company_Id === '00000000-0000-0000-0000-000000000000') {
        logger.warn('Proceeding without a valid company ID');
        logDebugInfo('Proceeding without a valid company ID', {
          environmentId: selectedEnvironment.id,
          environmentName: selectedEnvironment.name
        });
        
        // Use a placeholder value to avoid null references
        selectedEnvironment.company_Id = selectedEnvironment.company_Id || '00000000-0000-0000-0000-000000000000';
      }
      
      // Store the client company ID for future API calls
      localStorage.setItem('clientCompanyId', selectedEnvironment.company_Id);
      logger.log(`Stored client company ID in localStorage: ${selectedEnvironment.company_Id}`);
      
      // Update active environment in context/localStorage
      updateActiveEnvironment({
        id: selectedEnvironment.id,
        name: selectedEnvironment.name,
        type: selectedEnvironment.type || 'Production',
        url: selectedEnvironment.url || '',
        companyName: selectedEnvironment.companyName || selectedEnvironment.company_Name || '',
        company_Id: selectedEnvironment.company_Id // Include company_Id for subsequent API calls
      });
      
      logDebugInfo('Environment activated in local context, navigating to portal');
      
      // Navigate to portal
      navigate('/portal');
    } catch (error) {
      logger.error('Error in environment selection:', error);
      logDebugInfo('Error in environment selection', { message: error.message, details: error });
      setError(error.message || "An unexpected error occurred");
      setIsLoading(false);
    }
  }, [selectedEnvironment, updateActiveEnvironment, navigate, logDebugInfo, getAccessToken, tenantVerificationService]);

  // Function to handle direct "Return to Home" button click
  // FIXED: This function now performs a full cleanup and explicitly uses window.location
  const handleReturnHome = () => {
    logDebugInfo('Return to Home button clicked');
    
    // Clear any session state that might be causing navigation issues
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('activeEnvironment');
    localStorage.removeItem('registrationId');
    localStorage.removeItem('employeeNo');
    localStorage.removeItem('employeeId');
    localStorage.removeItem('clientCompanyId');
    localStorage.removeItem('clientCompanyName');
    localStorage.removeItem('clientCompanies');
    
    // Use window.location for a hard redirect to ensure it works
    window.location.href = '/';
  };

  // Debug section to display state information
  const DebugSection = () => {
    if (process.env.NODE_ENV !== 'development') return null;
    
    return (
      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="text-sm font-medium text-gray-900">Debug Information</h3>
        <div className="mt-2 text-xs text-gray-600 space-y-1">
          <div>User: {user ? `${user.name} (${user.email})` : 'No user'}</div>
          <div>Loading: {isLoading ? 'Yes' : 'No'}</div>
          <div>Error: {error || 'None'}</div>
          <div>Environments: {environments.length}</div>
          <div>Selected: {selectedEnvironment ? selectedEnvironment.name : 'None'}</div>
          <div>Redirect in Progress: {redirectInProgress ? 'Yes' : 'No'}</div>
          <pre className="mt-2 max-h-40 overflow-auto bg-gray-100 p-2 rounded">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            {bcAccessDenied ? (
              <>
                <h2 className="text-xl font-semibold text-gray-900">Access Not Provisioned</h2>
                <p className="mt-2 text-gray-600">
                  Your account has been authenticated successfully, but you have not been granted
                  access to SiriusConnect365. Please contact your administrator to request access.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-gray-900">Sign In Required</h2>
                <p className="mt-2 text-gray-600">
                  Your session could not be verified. Please sign in again to continue.
                </p>
              </>
            )}
          </div>
          <div className="mt-6">
            {bcAccessDenied ? (
              <button
                onClick={() => logout ? logout() : window.location.href = '/'}
                type="button"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Sign Out
              </button>
            ) : (
              <button
                onClick={() => window.location.href = '/login'}
                type="button"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Sign In
              </button>
            )}
          </div>
          <DebugSection />
        </div>
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your environments...</p>
          <DebugSection />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-indigo-600 px-6 py-4">
          <h2 className="text-xl font-semibold text-white">Select Business Central Environment</h2>
        </div>
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Please select the Business Central environment you want to access:
          </p>
          
          <div className="space-y-3 mb-8">
            {environments.length > 0 ? (
              environments.map((environment) => {
                // Check if this environment has a company_Id
                const hasCompanyId = !!environment.company_Id && 
                  environment.company_Id !== '00000000-0000-0000-0000-000000000000';
                
                // Always allow selection regardless of company_Id
                // This allows users to select environments that need setup
                
                return (
                  <div
                    key={environment.id}
                    className={`border rounded-lg p-4 transition-colors ${
                      selectedEnvironment?.id === environment.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-indigo-300 cursor-pointer'
                    }`}
                    onClick={() => handleSelect(environment)}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <Server className={`h-5 w-5 ${
                          selectedEnvironment?.id === environment.id ? 'text-indigo-600' : 'text-gray-400'
                        }`} />
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex justify-between">
                          <h3 className="text-sm font-medium text-gray-900">{environment.name}</h3>
                          {selectedEnvironment?.id === environment.id && (
                            <CheckCircle className="h-5 w-5 text-indigo-600" />
                          )}
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          Type: {environment.type}
                        </p>
                        {(environment.companyName || environment.company_Name) && (
                          <p className="text-sm text-gray-500">
                            Company: {environment.companyName || environment.company_Name}
                            {!hasCompanyId && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                                Ready
                              </span>
                            )}
                          </p>
                        )}
                        <p className="text-xs text-gray-400">
                          {environment.url}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-gray-500">No environments available</p>
            )}
          </div>
          
          <div className="mt-6 flex justify-between">
            <button
              onClick={handleReturnHome}
              type="button"
              className="flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Return to Home
            </button>
            
            <button
              onClick={handleContinue}
              type="button"
              disabled={!selectedEnvironment || isLoading}
              className={`flex justify-center py-2 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                !selectedEnvironment || isLoading
                  ? 'bg-indigo-300 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              }`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                'Continue to Portal'
              )}
            </button>
          </div>
          
          <DebugSection />
        </div>
      </div>
    </div>
  );
};

export default EnvironmentSelector;